// src/utils/pubnubFileUtils.ts
import PubNub from "pubnub";

// In-memory cache for blob URLs
const fileCache: Record<string, string> = {};
const downloadPromises: Record<string, Promise<string> | undefined> = {};

/**
 * Fetches a file from PubNub with proper authentication
 * Returns a blob URL that can be used for preview
 */
export const fetchPubNubFile = async (
  pubnub: PubNub,
  channelId: string,
  fileId: string,
  fileName: string,
): Promise<string> => {
  const cacheKey = `${channelId}-${fileId}-${fileName}`;

  // Check cache first
  // debugger;
  if (fileCache[cacheKey]) {
    return fileCache[cacheKey];
  }

  // Check if already downloading
  if (downloadPromises[cacheKey]) {
    console.log(`[PubNub] Already downloading: ${fileName}`);
    return downloadPromises[cacheKey]!;
  }

  console.log(
    `[PubNub] Downloading file: ${fileName} from channel: ${channelId} with ID: ${fileId}`,
  );

  // Store the promise to prevent multiple downloads
  downloadPromises[cacheKey] = new Promise(async (resolve, reject) => {
    try {
      // Download the file through PubNub
      const response = await pubnub.downloadFile({
        channel: channelId,
        id: fileId,
        name: fileName,
      });

      console.log(`[PubNub] Download response type:`, typeof response);
      console.log(`[PubNub] Download response:`, response);

      // Check what we got back
      if (!response) {
        throw new Error("Empty response from downloadFile");
      }

      // Handle different response types from PubNub
      let blob: Blob;

      // Case 1: Response is already a Blob
      if (response instanceof Blob) {
        blob = response;
      }
      // Case 2: Response has a data property that is a Blob
      else if (response.data && response.data instanceof Blob) {
        blob = response.data;
      }
      // Case 3: Response is an ArrayBuffer
      else if (response instanceof ArrayBuffer) {
        blob = new Blob([response]);
      }
      // Case 4: Response has buffer property (Node.js style)
      else if (response.buffer && response.buffer instanceof ArrayBuffer) {
        console.log(
          `[PubNub] Response has buffer property, converting to Blob`,
        );
        blob = new Blob([response.buffer]);
      }
      // Case 5: Response might be a string or object that needs to be converted
      else if (typeof response === "string") {
        console.log(`[PubNub] Response is string, converting to Blob`);
        blob = new Blob([response], { type: "text/plain" });
      }
      // Case 6: Response might be a PubNub File object with a stream
      else if (response.stream || response.body) {
        // Try to get the stream
        const stream = response.stream || response.body;
        if (stream) {
          const reader = stream.getReader();
          const chunks: Uint8Array[] = [];

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            chunks.push(value);
          }

          const totalLength = chunks.reduce(
            (acc, chunk) => acc + chunk.length,
            0,
          );
          const uint8Array = new Uint8Array(totalLength);
          let offset = 0;
          for (const chunk of chunks) {
            uint8Array.set(chunk, offset);
            offset += chunk.length;
          }

          blob = new Blob([uint8Array]);
        } else {
          throw new Error("Cannot read stream from response");
        }
      }
      // Case 7: Response might be an object with a url property (direct S3 URL)
      else if (response.url) {
        // Fetch the file from the URL
        const fetchResponse = await fetch(response.url);
        if (!fetchResponse.ok) {
          throw new Error(`Failed to fetch from URL: ${fetchResponse.status}`);
        }
        blob = await fetchResponse.blob();
      }
      // Last resort: try to stringify and create a blob
      else {
        console.log(`[PubNub] Unknown response type, trying to stringify`);
        console.log(`[PubNub] Response keys:`, Object.keys(response));

        try {
          const jsonString = JSON.stringify(response);
          blob = new Blob([jsonString], { type: "application/json" });
        } catch (e) {
          throw new Error(`Unexpected response type: ${typeof response}`);
        }
      }

      // Verify we have a valid blob
      if (!blob || !(blob instanceof Blob)) {
        throw new Error("Failed to create valid Blob from response");
      }

      console.log(
        `[PubNub] File downloaded successfully: ${fileName}, blob size: ${blob.size} bytes, type: ${blob.type}`,
      );

      // Create a blob URL for preview
      const blobUrl = URL.createObjectURL(blob);

      console.log(`[PubNub] Blob URL created: ${blobUrl}`);

      // Cache the URL
      fileCache[cacheKey] = blobUrl;

      // Clean up the promise reference
      delete downloadPromises[cacheKey];

      resolve(blobUrl);
    } catch (error: any) {
      console.error("[PubNub] Failed to fetch file:", error);

      // Clean up the promise reference on error
      delete downloadPromises[cacheKey];

      // More specific error messages
      if (error.status?.statusCode === 403) {
        reject(new Error("Access denied. Please check PubNub permissions."));
      } else if (error.status?.statusCode === 404) {
        reject(new Error("File not found."));
      } else if (error.message?.includes("timeout")) {
        reject(new Error("Download timed out. Please try again."));
      } else {
        reject(error);
      }
    }
  });

  return downloadPromises[cacheKey]!;
};

/**
 * Alternative approach using fetch API with PubNub-generated URL
 * This might work if the URL is accessible with proper authentication
 */
export const fetchPubNubFileViaUrl = async (
  pubnub: PubNub,
  channelId: string,
  fileId: string,
  fileName: string,
): Promise<string> => {
  const cacheKey = `${channelId}-${fileId}-${fileName}-url`;

  // Check cache first
  if (fileCache[cacheKey]) {
    return fileCache[cacheKey];
  }

  // Check if already downloading
  if (downloadPromises[cacheKey]) {
    console.log(`[PubNub] Already downloading via URL: ${fileName}`);
    return downloadPromises[cacheKey]!;
  }

  console.log(
    `[PubNub] Fetching file via URL: ${fileName} from channel: ${channelId}`,
  );

  downloadPromises[cacheKey] = new Promise(async (resolve, reject) => {
    try {
      // Get the file URL from PubNub
      const fileUrl = pubnub.getFileUrl({
        channel: channelId,
        id: fileId,
        name: fileName,
      });

      console.log(`[PubNub] File URL:`, fileUrl);

      // Fetch the file with credentials
      const response = await fetch(fileUrl, {
        credentials: "include", // Include cookies if needed
        headers: {
          Accept: "*/*",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      console.log(
        `[PubNub] File fetched via URL: ${fileName}, size: ${blob.size} bytes`,
      );

      const blobUrl = URL.createObjectURL(blob);

      // Cache the URL
      fileCache[cacheKey] = blobUrl;

      // Clean up the promise reference
      delete downloadPromises[cacheKey];

      resolve(blobUrl);
    } catch (error) {
      console.error("[PubNub] Failed to fetch file via URL:", error);

      // Clean up the promise reference on error
      delete downloadPromises[cacheKey];

      reject(error);
    }
  });

  return downloadPromises[cacheKey]!;
};

/**
 * Downloads a file from PubNub with proper authentication
 */
export const downloadPubNubFile = async (
  pubnub: PubNub,
  channelId: string,
  fileId: string,
  fileName: string,
): Promise<void> => {
  try {
    console.log(`[PubNub] Downloading file for save: ${fileName}`);

    // Try both methods
    let blob: Blob | null = null;
    let method = "unknown";

    // Method 1: Use downloadFile
    try {
      const response = await pubnub.downloadFile({
        channel: channelId,
        id: fileId,
        name: fileName,
      });

      // Extract blob from response
      if (response instanceof Blob) {
        blob = response;
        method = "downloadFile (direct Blob)";
      } else if (response.data && response.data instanceof Blob) {
        blob = response.data;
        method = "downloadFile (data.Blob)";
      } else if (response.buffer && response.buffer instanceof ArrayBuffer) {
        blob = new Blob([response.buffer]);
        method = "downloadFile (buffer)";
      } else if (response instanceof ArrayBuffer) {
        blob = new Blob([response]);
        method = "downloadFile (ArrayBuffer)";
      }
    } catch (error) {
      console.log("[PubNub] downloadFile method failed:", error);
    }

    // Method 2: Use URL if first method failed
    if (!blob) {
      try {
        const fileUrl = pubnub.getFileUrl({
          channel: channelId,
          id: fileId,
          name: fileName,
        });

        const fetchResponse = await fetch(fileUrl);
        if (fetchResponse.ok) {
          blob = await fetchResponse.blob();
          method = "URL fetch";
        }
      } catch (error) {
        console.log("[PubNub] URL fetch method failed:", error);
      }
    }

    if (!blob) {
      throw new Error("Could not download file using any method");
    }

    console.log(
      `[PubNub] File ready for download (${method}): ${fileName}, size: ${blob.size} bytes`,
    );

    // Create download link
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    // Clean up
    setTimeout(() => URL.revokeObjectURL(url), 100);
  } catch (error: any) {
    console.error("[PubNub] Failed to download file:", error);
    throw error;
  }
};

/**
 * Preloads a file (useful for images you want to show quickly)
 */
export const preloadPubNubFile = async (
  pubnub: PubNub,
  channelId: string,
  fileId: string,
  fileName: string,
): Promise<void> => {
  try {
    await fetchPubNubFile(pubnub, channelId, fileId, fileName);
  } catch (error) {
    console.warn("[PubNub] Failed to preload file:", error);
  }
};

/**
 * Gets file info from PubNub
 */
export const getPubNubFileInfo = async (
  pubnub: PubNub,
  channelId: string,
  fileId: string,
  fileName: string,
): Promise<any> => {
  try {
    // Try to get file info from PubNub
    // Note: This depends on your PubNub SDK version
    const response = await pubnub.listFiles({
      channel: channelId,
    });

    const fileInfo = response.data.find(
      (f: any) => f.id === fileId || f.name === fileName,
    );
    return fileInfo || null;
  } catch (error) {
    console.error("[PubNub] Failed to get file info:", error);
    return null;
  }
};

/**
 * Cleans up blob URLs to prevent memory leaks
 */
export const cleanupBlobUrl = (url: string) => {
  if (url && url.startsWith("blob:")) {
    URL.revokeObjectURL(url);

    // Also remove from cache
    Object.keys(fileCache).forEach((key) => {
      if (fileCache[key] === url) {
        delete fileCache[key];
      }
    });
  }
};

/**
 * Clears the entire file cache
 */
export const clearFileCache = () => {
  // Clean up all blob URLs
  Object.values(fileCache).forEach((url) => {
    if (url && url.startsWith("blob:")) {
      URL.revokeObjectURL(url);
    }
  });

  // Clear caches
  Object.keys(fileCache).forEach((key) => delete fileCache[key]);
  Object.keys(downloadPromises).forEach((key) => delete downloadPromises[key]);

  console.log("[PubNub] File cache cleared");
};

/**
 * Checks if a file is cached
 */
export const isFileCached = (
  channelId: string,
  fileId: string,
  fileName: string,
): boolean => {
  const cacheKey = `${channelId}-${fileId}-${fileName}`;
  return !!fileCache[cacheKey];
};

/**
 * Fetches multiple files from PubNub in parallel (with concurrency limit)
 * Returns a map of fileId -> blobUrl
 */
export const fetchMultiplePubNubFiles = async (
  pubnub: PubNub,
  channelId: string,
  files: { id: string; name: string }[],
  concurrency = 3,
): Promise<Record<string, string>> => {
  const results: Record<string, string> = {};

  // Process files in batches to avoid overwhelming the network
  for (let i = 0; i < files.length; i += concurrency) {
    const batch = files.slice(i, i + concurrency);

    const batchResults = await Promise.allSettled(
      batch.map(async (file) => {
        try {
          const url = await fetchPubNubFile(
            pubnub,
            channelId,
            file.id,
            file.name,
          );
          return { id: file.id, url };
        } catch {
          // Fallback to URL method
          const url = await fetchPubNubFileViaUrl(
            pubnub,
            channelId,
            file.id,
            file.name,
          );
          return { id: file.id, url };
        }
      }),
    );

    batchResults.forEach((result) => {
      if (result.status === "fulfilled") {
        results[result.value.id] = result.value.url;
      }
    });
  }

  return results;
};

/**
 * Downloads a specific file from a multi-file message securely via PubNub auth
 */
export const downloadPubNubFileSingle = async (
  pubnub: PubNub,
  channelId: string,
  fileId: string,
  fileName: string,
): Promise<void> => {
  return downloadPubNubFile(pubnub, channelId, fileId, fileName);
};

/**
 * Gets cache stats
 */
export const getFileCacheStats = (): { size: number; keys: string[] } => {
  return {
    size: Object.keys(fileCache).length,
    keys: Object.keys(fileCache),
  };
};
